import { Component, OnInit } from '@angular/core';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

/**
 * EBUG-317: campos que alguma seção do modal já exibe. Alteração em campo fora
 * desta lista caía num diálogo em branco — o log existia, mas não era auditável.
 * Ver createOtherChanges().
 */
const HANDLED_FIELDS = [
  'name', 'vacancy', 'maintenance', 'active', 'boatProfile', 'movementLocation', 'movementGroup',
  'beam', 'draft', 'length', 'commercialLength', 'manufactureYear', 'modelYear', 'mainColor',
  'crew', 'passengersDay', 'passengersNight', 'cabins', 'bathrooms', 'bowType', 'fuel',
  'flyBridge', 'twoTimeEngine', 'chassiHull',
  'tie', 'boatInsurer', 'operator', 'boatCustomers', 'boatAccessories', 'boatServices',
  'boatSailorLogsOld',
  'apprehend', 'independentTanks', 'marineWashMyBoat', 'marineRunMyEnginer', 'timeBetweenWash',
  'timeBetweenEngineRotation', 'fullTank', 'showAllCustomerData', 'slingMultiQueue',
  'availableOnApp',
];

/** Metadados do próprio registro de log — não são alteração da embarcação. */
const IGNORED_FIELDS = [
  'id', '_id', 'boatId', 'marinaId', 'logTime', 'user', 'origin', 'operationLog',
  'oldDatas', 'updateDate', 'boatSailorLogs',
];

const FIELD_LABELS = {
  shipyardModelId: 'Modelo/estaleiro (id)',
  insurerType: 'Tipo de seguro',
  reviewed: 'Revisado',
  registerDate: 'Data de cadastro',
};

@Component({
  selector: 'app-boat-log-modal',
  templateUrl: './boat-log-modal.component.html',
  styleUrls: ['./boat-log-modal.component.scss'],
})
export class BoatLogModalComponent implements OnInit {
  boatLog;
  selectTab = 0;

  otherChanges: { label: string; oldValue: string; newValue: string }[] = [];
  isOtherChange = false;
  hasAnyChange = false;

  owners;
  authorizeds;
  ownersOld;
  authorizedsOld;
  accessoryAccessory;
  accessoryAccessoryOld;
  accessoryOptional;
  accessoryOptionalOld;
  serviceBoats;
  serviceBoatsOld;
  sailors;
  sailorsOld;

  isGeneralChange = false;
  isCustomerOwnerChange =false;
  isCustomerAuthorizedChange =false;
  isHullChange = false;
  isTieChange = false;
  isInsurerChange = false;
  isOperatorChange = false;
  isAccessoryOptionalChange = false;
  isAccessoryAccessoryChange = false;
  isServiceChange = false;
  isSailorChange = false;
  isBoatRegistrationChange = false;

  isGeneralData = false;
  isInventory = false;
  isCustomers = false;
  isServices = false;
  isBoatRegistration = false;

  constructor(
    private config: DynamicDialogConfig,
    public dialogRef: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    if(!this.config.data.boatLog || !this.config.data.boatLog.oldDatas){
      // Faltava o return: o close() nao interrompe o ngOnInit, entao createLogs()
      // seguia e estourava em Object.entries(undefined). Acontece quando o EasyLog
      // grava o log sem oldDatas — caso da primeira alteracao de uma embarcacao que
      // ainda nao tinha log anterior (BoatLogService.save, oldLog == null). EBUG-317.
      this.dialogRef.close();
      return;
    }
    this.boatLog = this.config.data.boatLog;
    this.createLogs();
    this.setSelectTab();
  }

  createLogs() {
    this.verifyChanges()
    this.createLogCustomer();
    this.createLogAccessory();
    this.createLogServiceBoat();
    this.createOtherChanges();
  }

  setSelectTab() {
    this.isGeneralData = this.isGeneralChange
      || this.isCustomerOwnerChange
      || this.isCustomerAuthorizedChange
      || this.isHullChange
      || this.isTieChange
      || this.isInsurerChange
      || this.isOperatorChange
      || this.isSailorChange
      || this.isOtherChange;

    this.isBoatRegistration = this.isBoatRegistrationChange;
    this.isServices = this.isServiceChange;

    this.hasAnyChange = this.isGeneralData || this.isBoatRegistration
      || this.isInventory || this.isServices;

    if(this.isGeneralData){
      this.selectTab = 0;
    } else if(this.isBoatRegistration){
      this.selectTab = 1;
    } else if(this.isInventory){
      this.selectTab = 2;
    } else if(this.isServices){
      this.selectTab = 3;
    } else {
      // Nada reconhecido: mantém a primeira aba e o template exibe o aviso de
      // ausência de detalhes, em vez de um diálogo em branco (EBUG-317).
      this.selectTab = 0;
    }
  }

  /**
   * EBUG-317: monta as alterações de campos que nenhuma seção exibe, para que o
   * log nunca apareça vazio quando o backend registrou uma mudança de verdade.
   */
  createOtherChanges() {
    this.otherChanges = Object.keys(this.boatLog.oldDatas)
      .filter((field) => !HANDLED_FIELDS.includes(field) && !IGNORED_FIELDS.includes(field))
      .map((field) => ({
        label: FIELD_LABELS[field] || field,
        oldValue: this.formatLogValue(this.boatLog.oldDatas[field]),
        newValue: this.formatLogValue(this.boatLog[field]),
      }))
      .filter((change) => change.oldValue !== change.newValue);

    this.isOtherChange = this.otherChanges.length > 0;
  }

  private formatLogValue(value): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    if (typeof value === 'object') {
      const serialized = JSON.stringify(value);
      return serialized.length > 200 ? serialized.substring(0, 200).concat('…') : serialized;
    }
    return String(value);
  }

  createLogServiceBoat() {
    this.serviceBoats = this.boatLog.boatServices;
    this.serviceBoatsOld = this.boatLog.oldDatas.boatServices;
  }

  createLogAccessory() {
    const oldDatas = this.boatLog.oldDatas;
    if(this.isInventory){
      this.accessoryAccessory = this.boatLog.boatAccessories.filter(
        (ac) => ac.accessory.accessoryType === 'Accessory'
      );
      this.accessoryOptional = this.boatLog.boatAccessories.filter(
        (ac) => ac.accessory.accessoryType === 'Optional'
      );
      this.accessoryAccessoryOld = oldDatas.boatAccessories.filter(
        (ac) => ac.accessory.accessoryType === 'Accessory'
      );
      this.accessoryOptionalOld = oldDatas.boatAccessories.filter(
        (ac) => ac.accessory.accessoryType === 'Optional'
      );

      this.isAccessoryAccessoryChange = this.accessoryAccessory.length > 0 || this.accessoryAccessoryOld.length > 0;
      this.isAccessoryOptionalChange = this.accessoryOptional.length > 0 || this.accessoryOptionalOld.length > 0;
    }
  }

  createLogCustomer() {
    if(this.isCustomers){
      this.owners = this.boatLog.boatCustomers.filter(
        (bc) => bc.type === 'Owner'
      );
      this.authorizeds = this.boatLog.boatCustomers.filter(
        (bc) => bc.type !== 'Owner'
      );
      this.ownersOld = this.boatLog.oldDatas.boatCustomers.filter(
        (bc) => bc.type === 'Owner'
      );
      this.authorizedsOld = this.boatLog.oldDatas.boatCustomers.filter(
        (bc) => bc.type !== 'Owner'
      );

      this.isCustomerOwnerChange = this.owners.length > 0 || this.ownersOld.length > 0;
      this.isCustomerAuthorizedChange = this.authorizeds.length > 0 || this.authorizedsOld.length > 0;
    }
  }

  verifyChanges() {
    const oldDatasMap = new Map(Object.entries(this.boatLog.oldDatas));
    this.verifyChangesGeneral(oldDatasMap);
    this.verifyChangesCustomer(oldDatasMap);
    this.verifyChangesAcessory(oldDatasMap)
    this.verifyChangesHull(oldDatasMap);
    this.verifyChangesTie(oldDatasMap);
    this.verifyChangesInsurer(oldDatasMap);
    this.verifyChangesOperator(oldDatasMap);
    this.verifyChangesServiceBoat(oldDatasMap);
    this.verifyChangesSailor(oldDatasMap);
    this.verifyChangesBoatRegistration(oldDatasMap);
  }

  verifyChangesAcessory(oldDatasMap: Map<string, unknown>) {
    this.isInventory = oldDatasMap.has('boatAccessories');
  }

  verifyChangesGeneral(oldDatasMap: Map<string, unknown>) {
    const changeVacancy = oldDatasMap.has('vacancy');
    this.isGeneralChange = this.boatLog.oldDatas && (oldDatasMap.has('name')
      || changeVacancy || oldDatasMap.has('maintenance') || oldDatasMap.has('active'))
      || oldDatasMap.has('boatProfile')
      || oldDatasMap.has('movementLocation')
      || oldDatasMap.has('movementGroup')
    }

  verifyChangesServiceBoat(oldDatasMap: Map<string, unknown>) {
    this.isServiceChange = oldDatasMap.has('boatServices');
  }
  verifyChangesOperator(oldDatasMap: Map<string, unknown>) {
    this.isOperatorChange = oldDatasMap.has('operator');
  }
  verifyChangesInsurer(oldDatasMap: Map<string, unknown>) {
    this.isInsurerChange = oldDatasMap.has('boatInsurer');
  }
  verifyChangesTie(oldDatasMap: Map<string, unknown>) {
    this.isTieChange = oldDatasMap.has('tie');
  }

  verifyChangesHull( oldDatasMap: Map<string, unknown>){
    this.isHullChange = oldDatasMap.has('beam') || oldDatasMap.has('draft') || oldDatasMap.has('draft') || oldDatasMap.has('length') || oldDatasMap.has('commercialLength') || oldDatasMap.has('manufactureYear')
    || oldDatasMap.has('modelYear') || oldDatasMap.has('mainColor') || oldDatasMap.has('crew') || oldDatasMap.has('passengersDay') || oldDatasMap.has('passengersNight') || oldDatasMap.has('cabins')
    || oldDatasMap.has('bathrooms') || oldDatasMap.has('bowType') || oldDatasMap.has('fuel')
    || oldDatasMap.has('flyBridge') || oldDatasMap.has('twoTimeEngine') || oldDatasMap.has('chassiHull');
  }

  verifyChangesCustomer(oldDatasMap: Map<string, unknown>){
    this.isCustomers = oldDatasMap.has('boatCustomers');
  }

  verifyChangesSailor(oldDatasMap) {
    const sailorLogs = this.boatLog.boatSailorLogs;
    const oldSailorLogs = this.boatLog.oldDatas?.boatSailorLogsOld;

    if (oldDatasMap.has('boatSailorLogsOld') && (oldSailorLogs.length > 0 || sailorLogs.length > 0)) {
      this.sailors = sailorLogs;
      this.sailorsOld = oldSailorLogs;
      this.isSailorChange = sailorLogs.length > 0 || oldSailorLogs > 0 ;
    }
  }

  verifyChangesBoatRegistration(oldDatasMap: Map<string, unknown>) {
    this.isBoatRegistrationChange = oldDatasMap.has('apprehend')
      || oldDatasMap.has('independentTanks')
      || oldDatasMap.has('marineWashMyBoat')
      || oldDatasMap.has('marineRunMyEnginer')
      || oldDatasMap.has('timeBetweenWash')
      || oldDatasMap.has('timeBetweenEngineRotation')
      || oldDatasMap.has('fullTank')
      || oldDatasMap.has('showAllCustomerData')
      || oldDatasMap.has('slingMultiQueue')
      || oldDatasMap.has('active')
      || oldDatasMap.has('maintenance')
      || oldDatasMap.has('twoTimeEngine')
      || oldDatasMap.has('availableOnApp');
  }
}
